import { useMemo } from 'react'
import type { LookupValue } from '@/types/api/engagement'
import type { EngagementReferences } from '@/types/api/engagement'
import { useEngagementLookups } from './use-engagement-lookups'
import { useCodeValues } from './use-code-values'

/**
 * Resolves the display values shown in the audit-history diff view.
 *
 * Organization / profile names come from the server-resolved `references` (returned by the history
 * endpoint), NOT from the row-level-security gated org/UserProfile list endpoints — so a restricted
 * Engagement Lead, who can read the history but not those entities directly, still sees names rather
 * than bare ids. Code-table values (getValue) continue to resolve from the global lookup/code-value
 * caches, which are not RLS-gated.
 */
export function useAuditLookups(references: EngagementReferences) {
  // Use existing hooks for lookups and code values (global reference data, not RLS-gated)
  const engagementLookups = useEngagementLookups()
  const codeValues = useCodeValues()

  // Combined getValue that checks both engagement lookups and code values
  const getValue = useMemo(() => {
    return (category: string, code: string): LookupValue | undefined => {
      // Try engagement lookups first (API-based lookups)
      const lookupValue = engagementLookups.getValue(category, code)
      if (lookupValue) {
        return lookupValue
      }

      // Fall back to code values (static code values)
      const codeValue = codeValues.getCodeValueByCode(code, category)
      if (codeValue) {
        // Transform CodeItem to LookupValue format
        return {
          code: codeValue.code,
          value: codeValue.text,
          description: codeValue.text
        }
      }

      // Fall back to ID-based lookup for integer-valued lookup fields
      const numericId = Number(code)
      if (Number.isFinite(numericId) && /^-?\d+$/.test(code)) {
        const codeValueById = codeValues.getCodeValue(numericId, category)
        if (codeValueById) {
          return {
            code: codeValueById.code,
            value: codeValueById.text,
            description: codeValueById.text
          }
        }
      }

      return undefined
    }
  }, [engagementLookups, codeValues])

  return {
    getOrgName: (id: number) => references.organizations[id]?.displayName || `Organization ID ${id}`,
    getProfileName: (id: number) => {
      const fullName = references.profiles[id]?.fullName
      // Match form display format: "Name - 12345"
      return fullName ? `${fullName} - ${id}` : `Profile ID ${id}`
    },
    getAuditByName: (id: number) => references.profiles[id]?.fullName || `Profile ID ${id}`,
    getValue,
    // Only the global lookup/code-value caches gate readiness now; org/profile names arrive with the
    // history payload, so there's no separate fetch to await.
    loading: engagementLookups.loading || !codeValues.isReady
  }
}
